import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

/**
 * PUENTE-WP-CLUB · Pantalla intermedia del Single Sign-On WordPress → CRM.
 *
 * Recibe ?token=XXXX (token corto firmado por WordPress), lo canjea en
 * /auth/wordpress-token por el JWT propio del CRM y redirige al Canal.
 * Si el token caducó / es inválido / no hay cuenta, redirige al login de
 * WordPress con un mensaje claro (decisión Ann 2026-06-11).
 *
 * Ver docs/PUENTE_WORDPRESS_CRM_CLUB_SPEC.md
 */

// URL de login de WordPress para el fallback. Configurable por entorno.
// TODO (Pendiente D): confirmar la URL exacta del login de WordPress.
const WP_LOGIN_URL =
  process.env.REACT_APP_WP_LOGIN_URL ||
  '/entrar?puerta=canal'; // [Ann 2026-10-23] WordPress cerrado: siempre al login del canal

export default function WordpressTokenBridge() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { loginWithWordpressToken } = useAuth();
  const [error, setError] = useState(null);
  const yaProcesado = useRef(false); // evita doble ejecución (StrictMode)

  useEffect(() => {
    if (yaProcesado.current) return;
    yaProcesado.current = true;

    const token = searchParams.get('token');

    const irALoginWordpress = (mensaje) => {
      setError(mensaje);
      // Pequeña pausa para que la clienta lea el mensaje antes de redirigir.
      setTimeout(() => {
        window.location.href = WP_LOGIN_URL;
      }, 3500);
    };

    if (!token) {
      irALoginWordpress('Falta el enlace de acceso. Te llevamos al inicio de sesión.');
      return;
    }

    (async () => {
      try {
        await loginWithWordpressToken(token);
        navigate('/mi-espacio', { replace: true });
      } catch (e) {
        const code = e?.response?.data?.detail?.code;
        const msg = e?.response?.data?.detail?.msg;
        if (code === 'user_not_found_in_crm') {
          // No redirigimos a WordPress: la clienta debe contactar con la clínica.
          setError(
            msg ||
              'No encontramos tu cuenta en la plataforma. Contacta con la clínica para activarla.'
          );
          return;
        }
        irALoginWordpress(
          msg || 'Tu enlace de acceso ha caducado. Vuelve a entrar desde tu cuenta.'
        );
      }
    })();
  }, [searchParams, navigate, loginWithWordpressToken]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="text-center max-w-md">
        {!error ? (
          <>
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500 mx-auto mb-4" />
            <p className="text-gray-600">Accediendo a tu espacio…</p>
          </>
        ) : (
          <p className="text-gray-700">{error}</p>
        )}
      </div>
    </div>
  );
}
